import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MarketingLayout from '../components/marketing/MarketingLayout';
import '../styles/marketing-free-redlining.css';

/* ============================================================
   Free Redlining Review — standalone prospect landing page.
   The public top-of-funnel for CLARID-SPEC-FREE-REDLINING-001:
   a prospect enters their bank, and we generate a Redlining
   Risk Snapshot from public FDIC + census data (no upload),
   wired live to POST /public/redlining-review (T3).

   Standalone marketing chrome (logo-only header, no app shell),
   composed from the .clarid-marketing "banker-paper" system.
   ============================================================ */

// The public endpoint is mounted OUTSIDE the /api tenant middleware, so we hit
// the backend origin root, not the `/api` axios instance (which also carries auth).
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const PUBLIC_BASE = API_URL.replace(/\/api\/?$/, '');

type RiskLevel = 'LOW' | 'MEDIUM' | 'HIGH';

interface R12Row {
  assessmentAreaName: string;
  isTotal: boolean;
  numOffices: number;
  mmctTracts: number;
  totalTracts: number;
  mmctTractPct: number;
  officesInMmct: number;
  officesInMmctPct: number;
  officesInNmct: number;
  officesInNmctPct: number;
  mmctRepresentation: 'favorable' | 'concern' | 'neutral';
}

interface FldTab {
  riskFactor: string;
  overallRiskLevel: RiskLevel;
  r12Matrix?: R12Row[];
}

interface PublicRedliningResult {
  institution: {
    cert: number;
    name: string | null;
    city: string | null;
    state: string | null;
    regulatorCode: string;
    assetsInThousands: number | null;
  };
  referenceYear: number;
  officeCount: number;
  officesGeocoded: number;
  loanCount: number;
  opportunity: {
    mode: 'lending-gap' | 'market-geography';
    mmctTractsInMarket: number;
    /** Null in `market-geography` mode — lending penetration was not measured. */
    underservedMmctTracts: number | null;
  };
  /**
   * How the market and MMCT status were defined. The API has always returned
   * this; the page discarded it, so the on-screen parity claim was published
   * with neither definition stated — the exact gap Michael Wallace hit on
   * 2026-08-17 (CLARID-BUG-PUBREV-001), which the PDF fixed and the screen
   * did not. A visitor from a paid campaign sees this page and often never
   * requests the PDF.
   */
  methodology?: {
    footprintCounties: number;
    marketCounties: number;
    tractsInMarket: number;
    tractsClassified: number;
    mmctLabel: string;
    mmctDefinition: string;
  };
  fld: { overallRiskLevel: RiskLevel; tabs: FldTab[] };
}

const RISK_COPY: Record<RiskLevel, { label: string; tone: string; blurb: string }> = {
  LOW: {
    label: 'Lower risk',
    tone: 'good',
    blurb: 'Your branch footprint tracks the majority-minority geography of your market. A clean starting point — worth confirming at the loan level.',
  },
  MEDIUM: {
    label: 'Worth a closer look',
    tone: 'warn',
    blurb: 'Your office distribution shows some daylight from the majority-minority tracts in your assessment areas — the kind of pattern a regulator would ask about.',
  },
  HIGH: {
    label: 'Elevated — review recommended',
    tone: 'bad',
    blurb: 'Your branch footprint is notably under-represented in majority-minority tracts relative to your market. This is exactly the signal a redlining review is built to surface — before a regulator does.',
  },
};

export default function FreeRedliningReviewPage() {
  useEffect(() => {
    document.title = 'Free Redlining Review — Clarid';
  }, []);

  // Progressive form: step 1 = name + email, step 2 = institution details.
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [bankName, setBankName] = useState('');
  const [role, setRole] = useState('');
  const [useCert, setUseCert] = useState(false);
  const [cert, setCert] = useState('');
  const [year, setYear] = useState('');
  const [lei, setLei] = useState('');

  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<PublicRedliningResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  // Both fields gate step 1. A lead with no name is a CRM record nobody works.
  const step1Valid = emailValid && fullName.trim().length >= 2;

  function scrollToForm() {
    document.getElementById('get-snapshot')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (!step1Valid) return;
    // Capture the email now (fire-and-forget) so a prospect who bails on the institution step is
    // still a lead, not a lost address. The full snapshot POST enriches it on completion.
    void axios
      .post(`${PUBLIC_BASE}/public/redlining-review/lead`, { email: email.trim(), fullName: fullName.trim() })
      .catch(() => {}); // never block the step transition on this
    setStep(2);
  }

  async function submitReview(e: FormEvent) {
    e.preventDefault();
    if (status === 'loading') return;
    if (!useCert && bankName.trim().length < 2) {
      setErrorMsg('Enter your institution name (or switch to FDIC cert #).');
      setStatus('error');
      return;
    }
    if (useCert && !/^\d+$/.test(cert.trim())) {
      setErrorMsg('FDIC cert # must be a number.');
      setStatus('error');
      return;
    }

    const payload: Record<string, unknown> = { email: email.trim(), fullName: fullName.trim() };
    if (useCert) payload.cert = Number(cert.trim());
    else payload.name = bankName.trim();
    if (year.trim()) payload.year = Number(year.trim());
    if (role.trim()) payload.role = role.trim(); // lead metadata; ignored by the endpoint
    // LEI is optional and unlocks the loan-level layer. Only send a well-formed one,
    // so a partial entry silently falls back to the branch-footprint screen (not a 400).
    const cleanLei = lei.trim().toUpperCase();
    if (/^[A-Z0-9]{20}$/.test(cleanLei)) payload.lei = cleanLei;

    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await axios.post<PublicRedliningResult>(
        `${PUBLIC_BASE}/public/redlining-review`,
        payload,
        { timeout: 120_000 },
      );
      setResult(res.data);
      setStatus('done');
      setTimeout(
        () => document.getElementById('snapshot-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        80,
      );
    } catch (err) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      const code = ax.response?.status;
      if (code === 404) {
        setErrorMsg(
          ax.response?.data?.error ||
            "We couldn't match that institution to a public FDIC record. Double-check the name, or try your FDIC cert #.",
        );
      } else if (code === 429) {
        setErrorMsg("We're generating a lot of snapshots right now — give it a minute and try again.");
      } else if (code === 503) {
        setErrorMsg(
          ax.response?.data?.error ||
            "We couldn't reach the FDIC directory just now — please try again in a moment.",
        );
      } else {
        setErrorMsg("Something went wrong generating your snapshot. Try again, or email bobby@clarid.ai and we'll run it by hand.");
      }
      setStatus('error');
    }
  }

  const r12Total = useMemo<R12Row | null>(() => {
    if (!result) return null;
    const tab = result.fld.tabs.find((t) => t.riskFactor === 'R12_BRANCH');
    return tab?.r12Matrix?.find((r) => r.isTotal) ?? null;
  }, [result]);

  const risk = result ? RISK_COPY[result.fld.overallRiskLevel] : null;

  return (
    <MarketingLayout nav={false} revealDeps={[status, step]}>
      {/* Slim, focused header — logo only, single low-emphasis sign-in. */}
      <header className="fr-topbar">
        <div className="container fr-topbar-inner">
          <Link to="/" aria-label="Clarid home">
            <img src="/clarid-logo-marketing.png" alt="clarid.ai" className="fr-logo" />
          </Link>
          <Link to="/login" className="fr-signin">
            Sign in
          </Link>
        </div>
      </header>

      {/* ─── Hero ─────────────────────────────────────────────── */}
      <section className="fr-hero">
        <div className="container fr-hero-grid">
          <div className="fr-hero-copy">
            <span className="kicker">§ FREE REDLINING REVIEW</span>
            <h1 className="fr-h1 balance">
              See your redlining risk <span className="frame-quote">before your regulator does.</span>
            </h1>
            <p className="lead fr-hero-lead">
              A custom Redlining Risk Snapshot on your institution, built from public data — no LAR upload, no
              data agreement, no cost. Enter your bank and we screen your branch footprint against the
              majority-minority tracts of your markets in about a minute.
            </p>
            <div className="fr-hero-cta">
              <button type="button" className="btn btn-primary" onClick={scrollToForm}>
                Get my free snapshot →
              </button>
              <a href="#whats-inside" className="btn btn-ghost">
                See what&rsquo;s inside
              </a>
            </div>
            <ul className="fr-trust">
              <li>Built by former FDIC &amp; Federal Reserve examiners</li>
              <li>Census &amp; FDIC branch data</li>
              <li>No upload — public data only</li>
            </ul>
          </div>

          {/* Video preview — value before the ask. */}
          <div className="fr-hero-media scale-in">
            <figure className="fr-video">
              <video
                src="/clarid-demo.mp4"
                poster="/clarid-marketing-icon.png"
                controls
                playsInline
                preload="metadata"
                className="fr-video-el"
              />
              <figcaption className="fr-video-cap">
                <span className="pill">▶ Preview</span> The redlining map &amp; peer-parity view your snapshot draws from.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <hr className="rule fr-rule" />

      {/* ─── The form + live snapshot ─────────────────────────── */}
      <section className="section fr-form-section" id="get-snapshot">
        <div className="container fr-form-grid">
          <div className="fr-form-intro reveal">
            <span className="kicker kicker--ink">§ 01 · YOUR SNAPSHOT</span>
            <h2 className="h2 balance">One minute. No upload. Your real geography.</h2>
            <p className="lead">
              We resolve your institution against the FDIC, pull your public branch locations, delineate your
              preliminary assessment areas, and run the same branch-distribution redlining screen your regulators use — live,
              on this page. No sales call required to see it.
            </p>
            <p className="fr-fineprint">
              This is a preliminary screen for discussion, built entirely from public data — not an examination
              finding or an accusation. The full loan-level analysis happens in your 30-minute review.
            </p>
          </div>

          <div className="fr-form-card card-ink scale-in">
            {status === 'done' && result ? (
              <SnapshotResult result={result} r12Total={r12Total} risk={risk!} />
            ) : (
              <form onSubmit={step === 1 ? submitEmail : submitReview} className="fr-form" noValidate>
                <div className="fr-steps">
                  <span className={`fr-step ${step >= 1 ? 'on' : ''}`}>1 · You</span>
                  <span className="fr-step-sep" />
                  <span className={`fr-step ${step >= 2 ? 'on' : ''}`}>2 · Institution</span>
                </div>

                {step === 1 ? (
                  <>
                    {/*
                      Name is captured at step 1 alongside the email. Without it a
                      CRM contact is an address and nothing else — after the first
                      live test a prospect could only be found by typing their exact
                      email. One extra field is a small friction cost against a lead
                      nobody can work.
                    */}
                    <label className="fr-label" htmlFor="fr-name">
                      Your name
                    </label>
                    <input
                      id="fr-name"
                      type="text"
                      className="fr-input"
                      placeholder="Jane Whitfield"
                      value={fullName}
                      onChange={(ev) => setFullName(ev.target.value)}
                      autoComplete="name"
                      required
                    />
                    <label className="fr-label" htmlFor="fr-email">
                      Work email
                    </label>
                    <input
                      id="fr-email"
                      type="email"
                      className="fr-input"
                      placeholder="you@yourbank.com"
                      value={email}
                      onChange={(ev) => setEmail(ev.target.value)}
                      autoComplete="email"
                      required
                    />
                    <p className="fr-hint">We&rsquo;ll email your snapshot here. No spam, ever.</p>
                    <button type="submit" className="btn btn-primary btn-block" disabled={!step1Valid}>
                      Continue →
                    </button>
                  </>
                ) : (
                  <>
                    {!useCert ? (
                      <>
                        <label className="fr-label" htmlFor="fr-bank">
                          Institution name
                        </label>
                        <input
                          id="fr-bank"
                          type="text"
                          className="fr-input"
                          placeholder="e.g. Bank of Decatur County"
                          value={bankName}
                          onChange={(ev) => setBankName(ev.target.value)}
                          autoComplete="organization"
                          required
                        />
                      </>
                    ) : (
                      <>
                        <label className="fr-label" htmlFor="fr-cert">
                          FDIC certificate #
                        </label>
                        <input
                          id="fr-cert"
                          type="text"
                          inputMode="numeric"
                          className="fr-input"
                          placeholder="e.g. 12345"
                          value={cert}
                          onChange={(ev) => setCert(ev.target.value)}
                          required
                        />
                      </>
                    )}
                    <button
                      type="button"
                      className="fr-toggle"
                      onClick={() => setUseCert((v) => !v)}
                    >
                      {useCert ? '← Use institution name instead' : 'I know my FDIC cert # →'}
                    </button>

                    <div className="fr-row">
                      <div className="fr-col">
                        <label className="fr-label" htmlFor="fr-role">
                          Your role <span className="fr-opt">(optional)</span>
                        </label>
                        <input
                          id="fr-role"
                          type="text"
                          className="fr-input"
                          placeholder="e.g. Chief Compliance Officer"
                          value={role}
                          onChange={(ev) => setRole(ev.target.value)}
                        />
                      </div>
                      <div className="fr-col fr-col-year">
                        <label className="fr-label" htmlFor="fr-year">
                          Year <span className="fr-opt">(optional)</span>
                        </label>
                        <input
                          id="fr-year"
                          type="text"
                          inputMode="numeric"
                          className="fr-input"
                          placeholder="2024"
                          value={year}
                          onChange={(ev) => setYear(ev.target.value)}
                        />
                      </div>
                    </div>

                    <label className="fr-label" htmlFor="fr-lei">
                      LEI <span className="fr-opt">(optional — unlocks your loan-level lending pattern)</span>
                    </label>
                    <input
                      id="fr-lei"
                      type="text"
                      className="fr-input"
                      placeholder="20-character Legal Entity Identifier"
                      value={lei}
                      onChange={(ev) => setLei(ev.target.value)}
                      maxLength={20}
                      autoComplete="off"
                    />
                    <p className="fr-hint">
                      Most banks can skip this — we&rsquo;ll screen your branch footprint from public data either way.
                    </p>

                    <button type="submit" className="btn btn-primary btn-block" disabled={status === 'loading'}>
                      {status === 'loading' ? 'Generating your snapshot…' : 'Generate my snapshot →'}
                    </button>
                    <button type="button" className="fr-back" onClick={() => setStep(1)}>
                      ← Back
                    </button>
                  </>
                )}

                {status === 'error' && <p className="fr-error">{errorMsg}</p>}
                {status === 'loading' && (
                  <p className="fr-loading">
                    Resolving your institution, geocoding branches, and screening your market…
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ─── What you'll get ──────────────────────────────────── */}
      <section className="section fr-inside" id="whats-inside">
        <div className="container">
          <span className="kicker">§ 02 · WHAT YOU&rsquo;LL GET</span>
          <h2 className="h2 balance fr-section-h">Everything a redlining screen should show — and the step no one else offers.</h2>
          <div className="fr-cards">
            {[
              {
                n: '01',
                t: 'Your footprint vs. the map',
                b: 'Branch and LPO locations plotted against the majority-minority (MMCT) tracts in your assessment areas — the geographic-distribution view regulators open first.',
              },
              {
                n: '02',
                t: 'Peer & market parity',
                b: 'Target-tract penetration and volume against peer and aggregate HMDA, with a plain Low / Moderate / High read you can take to your board.',
              },
              {
                n: '03',
                t: '“What a regulator would ask”',
                b: 'A short narrative framing the findings the way a regulator would — so nothing in your next exam is a surprise.',
              },
              {
                n: '04',
                t: 'Where to lend to close the gap',
                b: 'The offensive step a defensive review can’t give you: the underserved MMCT tracts inside your market that represent real growth — not just risk.',
                accent: true,
              },
            ].map((c) => (
              <article key={c.n} className={`fr-card card reveal${c.accent ? ' fr-card-accent' : ''}`}>
                <span className="eyebrow-num">{c.n}</span>
                <h3 className="fr-card-t">{c.t}</h3>
                <p className="fr-card-b">{c.b}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─────────────────────────────────────── */}
      <section className="section-tight fr-how">
        <div className="container">
          <span className="kicker kicker--ink">§ 03 · HOW IT WORKS</span>
          <h2 className="h2 balance fr-section-h">No data leaves your bank — because you don&rsquo;t send any.</h2>
          <div className="fr-steps-grid">
            {[
              { n: '1', t: 'Tell us your bank', b: 'Just the name (or FDIC cert #). We resolve it against public FDIC records.' },
              { n: '2', t: 'We screen public data', b: 'Your public branch footprint + Census data, run through our examiner-built redlining engine — instantly.' },
              { n: '3', t: 'See it, then dig in', b: 'Read your snapshot on the spot, get the full 1-pager by email, and book a 30-minute walkthrough of the loan-level analysis.' },
            ].map((s) => (
              <div key={s.n} className="fr-how-step reveal">
                <span className="fr-how-num">{s.n}</span>
                <h3 className="fr-card-t">{s.t}</h3>
                <p className="fr-card-b">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Credibility ──────────────────────────────────────── */}
      <section className="section fr-cred">
        <div className="container fr-cred-inner">
          <div className="reveal">
            <span className="kicker">§ 04 · WHY TRUST THE READ</span>
            <h2 className="h2 balance fr-section-h">
              Built by a community banker, validated by former examiners.
            </h2>
          </div>
          <div className="fr-cred-grid">
            {[
              {
                t: 'Practitioners, not advisors',
                b: 'A community-bank CMO and former FDIC & Federal Reserve examiners — 30 years of fair lending practice at the Fed — full-time on the product.',
              },
              {
                t: 'Regulatory agency methodology, to the decimal',
                b: 'Clarid reproduces the regulators’ fair lending methodology exactly — the same R1/R12 redlining screens, not an approximation.',
              },
              {
                t: 'Defensible by construction',
                b: 'Deterministic, evidence-grade, SHA-256-verified. The same inputs yield the same answer — which is exactly what a regulator asks for.',
              },
            ].map((c) => (
              <article key={c.t} className="fr-cred-card reveal">
                <h3 className="fr-card-t">{c.t}</h3>
                <p className="fr-card-b">{c.b}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────── */}
      <section className="section-tight fr-faq">
        <div className="container fr-faq-inner">
          <h2 className="h2 balance fr-section-h">Questions banks ask us</h2>
          {[
            {
              q: 'Is this really free?',
              a: 'Yes. The snapshot runs on public FDIC and Census data, so there’s no cost and nothing to sign. It’s the front door to a conversation, not a paid engagement.',
            },
            {
              q: 'Do I have to send you our loan data?',
              a: 'No. The v1 snapshot uses only your public branch footprint and public Census geography. The deeper loan-level analysis, if you want it, happens in your review — on your terms.',
            },
            {
              q: 'Is a snapshot an accusation of redlining?',
              a: 'No. It’s a preliminary screen for discussion, built from public data. It flags where a regulator might ask questions so you can get ahead of them — it is not an examination finding.',
            },
            {
              q: 'How current is the data?',
              a: 'Branch locations are from the latest public FDIC filings; geography is from the Census vintage matched to your analysis year. Public HMDA aggregate data for 2025 are now available.',
            },
          ].map((f) => (
            <details key={f.q} className="fr-faq-item reveal">
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ─── Final CTA ────────────────────────────────────────── */}
      <section className="fr-final">
        <div className="container fr-final-inner">
          <h2 className="fr-final-h balance">Know your redlining risk before your next exam.</h2>
          <p className="fr-final-sub">Run a free snapshot on your institution in about a minute.</p>
          <button type="button" className="btn btn-on-ink" onClick={scrollToForm}>
            Get my free snapshot →
          </button>
        </div>
      </section>
    </MarketingLayout>
  );
}

/* ── Live snapshot teaser (rendered in place of the form on success) ── */
function SnapshotResult({
  result,
  r12Total,
  risk,
}: {
  result: PublicRedliningResult;
  r12Total: R12Row | null;
  risk: { label: string; tone: string; blurb: string };
}) {
  const { institution: inst, referenceYear, officeCount, officesGeocoded, loanCount, opportunity, methodology } = result;
  const place = [inst.city, inst.state].filter(Boolean).join(', ');
  const assets =
    inst.assetsInThousands != null
      ? `$${(inst.assetsInThousands / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}B in assets`
      : null;
  const pdfUrl = `${PUBLIC_BASE}/public/redlining-review/snapshot.pdf?cert=${inst.cert}&year=${referenceYear}`;

  return (
    <div id="snapshot-result" className="fr-result">
      <div className="fr-result-head">
        <span className="pill">✓ Your snapshot · {referenceYear}</span>
        <span className={`fr-risk fr-risk-${risk.tone}`}>{risk.label}</span>
      </div>

      <h3 className="fr-result-name">{inst.name || `FDIC #${inst.cert}`}</h3>
      <p className="fr-result-meta">
        {[place, inst.regulatorCode ? `${inst.regulatorCode}-regulated` : null, assets]
          .filter(Boolean)
          .join(' · ')}
      </p>

      <p className="fr-result-blurb">{risk.blurb}</p>

      <div className="fr-tiles">
        <div className="fr-tile">
          <span className="fr-tile-n">{officeCount}</span>
          <span className="fr-tile-l">offices in your footprint</span>
        </div>
        {r12Total && (
          <>
            <div className="fr-tile">
              <span className="fr-tile-n">
                {r12Total.officesInMmct}
                <span className="fr-tile-of">/{r12Total.numOffices}</span>
              </span>
              <span className="fr-tile-l">offices in majority-minority tracts</span>
            </div>
            <div className="fr-tile">
              <span className="fr-tile-n">{Math.round(r12Total.mmctTractPct)}%</span>
              <span className="fr-tile-l">of your market&rsquo;s tracts are majority-minority</span>
            </div>
          </>
        )}
      </div>

      {r12Total && (
        <p className="fr-result-read">
          Your offices sit in majority-minority tracts{' '}
          <strong>{Math.round(r12Total.officesInMmctPct)}%</strong> of the time, against a market that is{' '}
          <strong>{Math.round(r12Total.mmctTractPct)}%</strong> majority-minority —{' '}
          {r12Total.mmctRepresentation === 'favorable'
            ? 'at or above parity.'
            : r12Total.mmctRepresentation === 'concern'
              ? 'below parity, the direction that draws regulator attention.'
              : 'no majority-minority tracts were identified in your delineated market.'}
        </p>
      )}

      {/*
        The definitions behind the two numbers above, on screen and not only in
        the PDF. A parity claim a recipient cannot reproduce is the thing that
        cost us credibility with Michael Wallace in August; a visitor arriving
        from a paid campaign sees this page and frequently never requests the PDF.
      */}
      {methodology && r12Total && (
        <p className="fr-result-note">
          <strong>How this was calculated.</strong> Your market is the{' '}
          {methodology.marketCounties} count{methodology.marketCounties === 1 ? 'y' : 'ies'} containing
          your offices — no MSA or contiguous expansion — covering{' '}
          {methodology.tractsInMarket.toLocaleString()} Census tracts, of which{' '}
          {methodology.tractsClassified.toLocaleString()} carry current Census reference data and form
          the basis of both figures. &ldquo;Majority-minority&rdquo; uses the{' '}
          {methodology.mmctLabel} definition — {methodology.mmctDefinition}. Every input is public
          FDIC and Census data, so these numbers can be reproduced independently.
        </p>
      )}

      {loanCount > 0 && (
        <p className="fr-result-loans">
          ✓ Loan-level: <strong>{loanCount.toLocaleString()}</strong> of your originations analyzed from public
          HMDA data — no upload required.
        </p>
      )}

      <p className="fr-result-note">
        Based on {officesGeocoded} of {officeCount} offices geocoded to a Census tract
        {loanCount > 0
          ? '. The full, unredacted loan-level fair lending analysis comes in your review.'
          : '. This is the branch-footprint screen; loan-level penetration and peer parity come in your review.'}
      </p>

      {/*
        Only claim a lending gap when we actually measured lending. In
        `market-geography` mode `underservedMmctTracts` is null: we know how many
        majority-minority tracts are in the market but nothing about penetration,
        so calling them all tracts the bank "could be reaching" counted the ones
        it already has offices in. State the count for what it is instead.
      */}
      {opportunity &&
        (opportunity.mode === 'lending-gap'
          ? (opportunity.underservedMmctTracts ?? 0) > 0
          : opportunity.mmctTractsInMarket > 0) && (
        <div className="fr-opp">
          <div className="fr-opp-head">
            <span className="pill">◆ Growth opportunity</span>
            <span className="fr-opp-locktag">🔒 Full map in your review</span>
          </div>
          {opportunity.mode === 'lending-gap' ? (
            <p className="fr-opp-stat">
              <strong>{(opportunity.underservedMmctTracts ?? 0).toLocaleString()}</strong>{' '}
              majority-minority{' '}
              {opportunity.underservedMmctTracts === 1 ? 'tract' : 'tracts'} in your market with{' '}
              <strong>zero originations</strong> in {referenceYear}.{' '}
              <span className="fr-opp-qualifier">
                Measured on originated loans from your public modified LAR, not applications.
              </span>
            </p>
          ) : (
            <p className="fr-opp-stat">
              <strong>{opportunity.mmctTractsInMarket.toLocaleString()}</strong> majority-minority{' '}
              {opportunity.mmctTractsInMarket === 1 ? 'tract' : 'tracts'} in your market.{' '}
              <span className="fr-opp-qualifier">
                Your lending penetration into them isn&rsquo;t measured on this screen — that comes
                with the loan-level review.
              </span>
            </p>
          )}
          <div className="fr-opp-map" aria-hidden="true">
            <div className="fr-opp-grid">
              {Array.from({ length: 48 }).map((_, i) => (
                <span
                  key={i}
                  className="fr-opp-cell"
                  style={{ opacity: 0.22 + ((i * 37) % 60) / 100 }}
                />
              ))}
            </div>
            <div className="fr-opp-lockoverlay">🔒 Unlock the opportunity map</div>
          </div>
          <p className="fr-opp-sub">
            That&rsquo;s not just fair lending exposure — it&rsquo;s mortgage volume you&rsquo;re leaving on the
            table. Clarid maps the <em>exact</em> tracts to lend in to close the gap.
          </p>
        </div>
      )}

      <div className="fr-result-cta">
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-ghost btn-block fr-dl"
        >
          ↓ Download your 1-page snapshot (PDF)
        </a>
        <Link to="/request-demo" className="btn btn-primary btn-block">
          Book my 30-minute review →
        </Link>
        <p className="fr-hint fr-center">
          Check your inbox — we&rsquo;ve emailed your snapshot. Questions first?{' '}
          <a href="mailto:bobby@clarid.ai">bobby@clarid.ai</a>
        </p>
      </div>
    </div>
  );
}
